import { STEP_UP_LOGIN_MINT_MESSAGE, STEP_UP_REQUIRED_ERROR } from '@/lib/access/step-up';
import { replaceSignInLinkActionInHistory } from '@/lib/sign-in-link-callback';

export const LOGIN_NOTICE_STORAGE_KEY = 'harry_login_notice';

export type LoginNoticeKind = 'stale-session' | 'link-used' | 'link-expired' | 'link-failed';

export const LOGIN_NOTICE_COPY: Record<LoginNoticeKind, { title: string; description: string }> = {
  'stale-session': {
    title: 'Sign in again to continue',
    description: STEP_UP_LOGIN_MINT_MESSAGE,
  },
  'link-used': {
    title: 'This email link was already used',
    description:
      'Each sign-in link works once. Request a new link below with the email you were invited with, or use Sign in with Google.',
  },
  'link-expired': {
    title: 'This email link has expired',
    description:
      'Request a new link below with the email you were invited with, or use Sign in with Google.',
  },
  'link-failed': {
    title: 'Could not complete the email link',
    description: 'Request a new sign-in link below, or use Sign in with Google.',
  },
};

export function loginNoticeFromFirebaseLinkError(code?: string): LoginNoticeKind {
  if (code === 'auth/expired-action-code') return 'link-expired';
  if (code === 'auth/invalid-action-code') return 'link-used';
  return 'link-failed';
}

export function writeLoginNotice(kind: LoginNoticeKind): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(LOGIN_NOTICE_STORAGE_KEY, kind);
}

export function readLoginNotice(): LoginNoticeKind | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(LOGIN_NOTICE_STORAGE_KEY);
  if (raw === 'stale-session' || raw === 'link-used' || raw === 'link-expired' || raw === 'link-failed') {
    return raw;
  }
  return null;
}

export function clearLoginNotice(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(LOGIN_NOTICE_STORAGE_KEY);
}

export type Login403Action = { kind: 'stay-on-login' } | { kind: 'redirect'; path: string };

export function classifyLogin403Body(body: unknown): Login403Action {
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const reasonKind =
    record.reason && typeof record.reason === 'object'
      ? (record.reason as { kind?: unknown }).kind
      : undefined;
  if (record.error === STEP_UP_REQUIRED_ERROR || reasonKind === STEP_UP_REQUIRED_ERROR) {
    return { kind: 'stay-on-login' };
  }
  const redirectTo = record.redirectTo;
  const path =
    typeof redirectTo === 'string' && redirectTo.startsWith('/') ? redirectTo : '/signup-required';
  return { kind: 'redirect', path };
}

/** Persist on-login help and drop the one-time oobCode so Google is not blocked by a dead link. */
export function rememberStaleSessionOnLogin(): void {
  writeLoginNotice('stale-session');
  replaceSignInLinkActionInHistory();
}
